import { get } from 'lodash';

import { getHighlightedObservationIds } from '../../utils/observationHighlightUtil';

/** How Zoom outbound hooks should honor Communication Preferences observations. */
export type CommunicationChannelPreference = 'both' | 'email_only' | 'text_only' | 'none';

/** Patient profile search bundle shape used when resolving communication preferences (e.g. Patient 360 `profileData`). */
export type ProfileBundleForCommPreference =
  | {
      entry?: Array<{ resource?: Record<string, unknown> }>;
    }
  | undefined;

/** Preference value that blocks all automated outreach. */
const DO_NOT_CONTACT = 'Do Not Contact';

/** Phone-only preferences; block digital hooks unless Email or Text is also selected. */
const PHONE_ONLY_PREFERENCES = new Set(['Phone Only', 'Phone']);

/**
 * Derives channel behavior from active `COMMUNICATION-PREFERENCES` observations on the bundle.
 *
 * @remarks
 * Only observations that render as active (teal) chips in the profile UI are considered; grey (stale) rows are ignored.
 * Members can have multiple active rows (e.g. separate **Email** and **Text** observations). Channels are merged:
 * send mail if **any** active row is Email, send SMS if **any** active row is Text, send both when both appear.
 * **Phone Only** / **Phone** block digital hooks only when no active Email or Text row exists.
 * **Do Not Contact** always blocks automated outreach when it is an active row.
 * When every communication preference row is inactive, defaults to `both` (same as having no preferences on file).
 *
 * @param profileBundle - FHIR bundle or search result containing Observation entries.
 * @returns `both` when there are no such observations or only unrecognized values.
 */
export function getCommunicationChannelPreference(
  profileBundle: ProfileBundleForCommPreference
): CommunicationChannelPreference {
  const entries = get(profileBundle, 'entry', []) as Array<{ resource?: Record<string, unknown> }>;
  const commObs = entries.filter((e) => {
    const code = get<(typeof entries)[number], string, string | undefined>(
      e,
      'resource.code.coding[0].code',
      undefined
    );
    return typeof code === 'string' && code.indexOf('COMMUNICATION-PREFERENCES') !== -1;
  });
  if (commObs.length === 0) {
    return 'both';
  }

  const activeObservationIds = new Set(getHighlightedObservationIds(commObs));
  const activeCommObs = commObs.filter((obs) => {
    const id = get(obs, 'resource.id') as string | undefined;
    return typeof id === 'string' && activeObservationIds.has(id);
  });
  if (activeCommObs.length === 0) {
    return 'both';
  }

  const selectedPreferences = new Set<string>();
  for (const obs of activeCommObs) {
    const value = get(obs, 'resource.valueString') as string | undefined;
    if (value) {
      selectedPreferences.add(value);
    }
  }
  if (selectedPreferences.size === 0) {
    return 'both';
  }

  const wantsEmail = selectedPreferences.has('Email');
  const wantsText = selectedPreferences.has('Text');

  if (wantsEmail && wantsText) {
    return 'both';
  }
  if (wantsEmail) {
    return 'email_only';
  }
  if (wantsText) {
    return 'text_only';
  }

  if (selectedPreferences.has(DO_NOT_CONTACT)) {
    return 'none';
  }

  for (const v of selectedPreferences) {
    if (PHONE_ONLY_PREFERENCES.has(v)) {
      return 'none';
    }
  }

  return 'both';
}
